import React, { useState, useMemo, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { searchTransactions } from "../../store/modules/transactions/transactionActions";
import { GoogleMap, MarkerF, InfoWindowF } from "@react-google-maps/api";
import { PageLayout } from "../../ui-components/PageLayout";
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Paper,
  CircularProgress,
  Breadcrumbs,
  Card,
  CardContent,
  Divider,
  Button,
  Checkbox,
  ListItemText,
  TextField,
  Chip,
  OutlinedInput,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useSelector } from "react-redux";
import { useParams, useNavigate, Link } from "react-router-dom";
import ViewListIcon from "@mui/icons-material/ViewList";
import MapIcon from "@mui/icons-material/Map";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { RootState } from "../../store/reducers";
import LoadingDialog from "../../ui-components/Dialogs/LoadingDialog";
import { YearFilterSelect } from "../../ui-components/YearFilterSelect";
import {
  isPipelineStatus,
  statusChipSx,
  statusColor,
  transactionStatusLabel,
  usePipelineFolder,
} from "../../hooks/usePipeline";
import {
  IAddressModel,
  ILookupItemSimpleModel,
  ITransactionSearchModel,
  ReferrerTypeEnumModel,
} from "../../types/types";
import ReferrarAutoComplete from "../../ui-components/AutoComplete/ReferrarAutoComplete";
import { Dayjs } from "dayjs";

interface TransactionSearchFilters {
  clientName: string;
  typeId: number[];
  sourceId: number[];
  reasonId: number[];
  address: IAddressModel | null;
  priceMin: number | "";
  priceMax: number | "";
  dateFrom: Dayjs | null;
  dateTo: Dayjs | null;
  yearIds: number[];
  zipCode: string;
  county: string;
  areaId: number[];
  specialTags: number[];
  specialGroups: number[];
  coAgentId: number | "";
  referralOutTypeId: number[];
  soiReferrers: ILookupItemSimpleModel[];
  agentReferrers: ILookupItemSimpleModel[];
  vendorReferrers: ILookupItemSimpleModel[];
  /** Under Contract entries (Pipeline folder); "Active" in the years menu brings Active entries only */
  includeUnderContracts: boolean;
}

type ViewMode = "list" | "map";

const TransactionSearchAndMapPage: React.FC = () => {
  const navigate = useNavigate();
  const { year } = useParams<{ year: string }>();

  const { years } = useSelector((state: RootState) => state.transactionYear);
  const { data: userData } = useSelector(
    (state: RootState) => state.commonData.userData,
  );
  const { selectedCoAgentIds } = useSelector(
    (state: RootState) => state.coAgentFilter,
  );

  const { data: pipelineFolder } = usePipelineFolder();
  const pipelineYearId = pipelineFolder?.id;
  const showActive = pipelineYearId !== undefined;

  const isValidYear = year ? !isNaN(Number(year)) : false;
  const derivedYearId = isValidYear
    ? years?.find((y) => y.year === Number(year))?.id
    : undefined;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [viewMode, setViewMode] = useState<ViewMode>("list");
  const [searchFilters, setSearchFilters] = useState<TransactionSearchFilters>({
    clientName: "",
    typeId: [],
    sourceId: [],
    reasonId: [],
    address: null,
    priceMin: "",
    priceMax: "",
    dateFrom: null,
    dateTo: null,
    yearIds: derivedYearId ? [derivedYearId] : [],
    zipCode: "",
    county: "",
    areaId: [],
    specialTags: [],
    specialGroups: [],
    coAgentId: "",
    referralOutTypeId: [],
    soiReferrers: [],
    agentReferrers: [],
    vendorReferrers: [],
    includeUnderContracts: false,
  });

  const [hasSearched, setHasSearched] = useState(false);

  const hasAnySearchCriteria = () => {
    return true; // Empty yearIds is valid (means "All Years")
  };

  const {
    data: searchResults,
    isLoading: isSearching,
    error,
    refetch,
  } = useQuery({
    queryKey: [
      "searchTransactions",
      selectedCoAgentIds,
      searchFilters.yearIds,
      searchFilters.includeUnderContracts,
      searchFilters.typeId,
      searchFilters.sourceId,
      searchFilters.reasonId,
      searchFilters.clientName,
      searchFilters.priceMin,
      searchFilters.priceMax,
      searchFilters.dateFrom?.toISOString(),
      searchFilters.dateTo?.toISOString(),
      searchFilters.address,
      searchFilters.zipCode,
      searchFilters.county,
      searchFilters.specialGroups,
      searchFilters.specialTags,
      searchFilters.coAgentId,
      searchFilters.referralOutTypeId,
      searchFilters.soiReferrers,
      searchFilters.agentReferrers,
      searchFilters.vendorReferrers,
    ],
    queryFn: async () => {
      const result = await searchTransactions(
        searchFilters.yearIds,
        searchFilters.typeId.length > 0 ? searchFilters.typeId : undefined,
        searchFilters.sourceId.length > 0 ? searchFilters.sourceId : undefined,
        searchFilters.reasonId.length > 0 ? searchFilters.reasonId : undefined,
        searchFilters.areaId.length > 0 ? searchFilters.areaId : undefined,
        searchFilters.specialTags.length > 0
          ? searchFilters.specialTags
          : undefined,
        searchFilters.specialGroups.length > 0
          ? searchFilters.specialGroups
          : undefined,
        searchFilters.coAgentId ? Number(searchFilters.coAgentId) : undefined,
        searchFilters.zipCode || undefined,
        searchFilters.county || undefined,
        searchFilters.clientName || undefined,
        searchFilters.priceMin ? Number(searchFilters.priceMin) : undefined,
        searchFilters.priceMax ? Number(searchFilters.priceMax) : undefined,
        searchFilters.address?.formattedAddress ?? undefined,
        searchFilters.dateFrom || undefined,
        searchFilters.dateTo || undefined,
        searchFilters.referralOutTypeId.length > 0
          ? searchFilters.referralOutTypeId
          : undefined,
        searchFilters.soiReferrers.length > 0
          ? searchFilters.soiReferrers.map((r) => r.id!)
          : undefined,
        searchFilters.agentReferrers.length > 0
          ? searchFilters.agentReferrers.map((r) => r.id!)
          : undefined,
        searchFilters.vendorReferrers.length > 0
          ? searchFilters.vendorReferrers.map((r) => r.id!)
          : undefined,
        searchFilters.includeUnderContracts,

        // searchFilters.clientName || undefined,
        // searchFilters.priceMin ? Number(searchFilters.priceMin) : undefined,
        // searchFilters.priceMax ? Number(searchFilters.priceMax) : undefined,
        // searchFilters.dateFrom || undefined,
        // searchFilters.dateTo || undefined,
        // searchFilters.address || undefined,
        // 1, // pageIndex
        // 10000 // pageSize - large number to get all results
      );
      return result;
    },
    enabled: false, // Disable automatic execution
    refetchOnWindowFocus: false,
  });

  const handleSearch = async () => {
    if (!hasAnySearchCriteria()) {
      return;
    }
    setHasSearched(true);
    refetch();
  };

  const clearAllFilters = () => {
    setSearchFilters({
      clientName: "",
      typeId: [],
      sourceId: [],
      reasonId: [],
      address: null,
      priceMin: "",
      priceMax: "",
      dateFrom: null,
      dateTo: null,
      yearIds: derivedYearId ? [derivedYearId] : [],
      zipCode: "",
      county: "",
      areaId: [],
      specialTags: [],
      specialGroups: [],
      coAgentId: "",
      referralOutTypeId: [],
      soiReferrers: [],
      agentReferrers: [],
      vendorReferrers: [],
      includeUnderContracts: false,
    });
    setHasSearched(false);
  };
  // Filter transactions with valid coordinates for map display
  const transactionsWithCoordinates = useMemo(() => {
    return (
      searchResults?.filter(
        (transaction: ITransactionSearchModel) =>
          transaction.latitude && transaction.longitude,
      ) ?? []
    );
  }, [searchResults]);

  /*
  const transactionsWithCoordinates =
    searchResults?.filter(
      (transaction: ITransactionSearchModel) =>
        transaction.latitude && transaction.longitude
    ) ?? [];
  */
  // Map functionality - only load when needed
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const mapBounds = useMemo(() => {
    if (transactionsWithCoordinates.length === 0) {
      return null;
    }

    if (transactionsWithCoordinates.length === 1) {
      return {
        center: {
          lat: transactionsWithCoordinates[0]?.latitude!,
          lng: transactionsWithCoordinates[0]?.longitude!,
        },
        zoom: 12,
      };
    }

    const lats = transactionsWithCoordinates.map(
      (t: ITransactionSearchModel) => t.latitude!,
    );
    const lngs = transactionsWithCoordinates.map(
      (t: ITransactionSearchModel) => t.longitude!,
    );

    return {
      north: Math.max(...lats),
      south: Math.min(...lats),
      east: Math.max(...lngs),
      west: Math.min(...lngs),
    };
  }, [transactionsWithCoordinates]);

  const summaryStats = useMemo(() => {
    const transactions = searchResults || [];
    const totalUnits = transactions.length;
    const totalVolume = transactions.reduce(
      (sum: number, t: ITransactionSearchModel) => sum + (t.price || 0),
      0,
    );
    const averagePrice = totalUnits > 0 ? totalVolume / totalUnits : 0;
    const totalGCI = transactions.reduce(
      (sum: number, t: ITransactionSearchModel) =>
        sum + (t.grossCommIncomeAmt || 0),
      0,
    );
    const totalNetIncome = transactions.reduce(
      (sum: number, t: ITransactionSearchModel) => sum + (t.netIncome || 0),
      0,
    );

    return {
      totalUnits,
      totalVolume,
      averagePrice,
      totalGCI,
      totalNetIncome,
    };
  }, [searchResults]);

  // The view/edit pages find the entry's folder themselves (Pipeline entries have no year folder)
  const handleViewTransaction = (transactionId: number) => {
    navigate(`/transactions/view/${transactionId}`);
  };

  const handleEditTransaction = (transactionId: number) => {
    navigate(`/transactions/edit/${transactionId}`);
  };

  const formatPrice = (price: number | undefined) => {
    if (!price) return "-";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const formatDate = (date: Dayjs | undefined) => {
    if (!date) return "-";
    return date.format("MM/DD/YYYY");
  };

  const getTypeName = (typeId: number | undefined) => {
    return (
      userData?.transactionTypes?.find((t) => t.id === typeId)?.name || "-"
    );
  };

  const getSourceName = (sourceId: number | undefined) => {
    return userData?.sources?.find((s) => s.id === sourceId)?.name || "-";
  };

  const renderLimitedChips = (
    items: Array<{ key: number; label: string | number }>,
    maxVisible = 2,
  ) => {
    const visible = items.slice(0, maxVisible);
    const remaining = items.length - visible.length;

    return (
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
        {visible.map((item) => (
          <Chip key={item.key} label={item.label} size="small" />
        ))}
        {remaining > 0 && (
          <Chip key="more" label={`+${remaining} more`} size="small" />
        )}
      </Box>
    );
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const getClientNames = (clients: any[] | undefined) => {
    if (!clients || clients.length === 0) return "-";
    return clients.map((c) => c.name).join(", ");
  };

  // Google Maps is now loaded globally via GoogleMapsProvider in App.tsx
  // No need to load it again here - it's already available

  // Map View Component (memoized, manages its own selectedTransaction)
  const MapView: React.FC<{
    transactionsWithCoordinates: ITransactionSearchModel[];
    formatPrice: (price: number | undefined) => string;
    getTypeName: (typeId: number | undefined) => string;
    getSourceName: (sourceId: number | undefined) => string;
    navigate: ReturnType<typeof useNavigate>;
  }> = React.memo(
    ({
      transactionsWithCoordinates,
      formatPrice,
      getTypeName,
      getSourceName,
      navigate,
    }) => {
      const [selectedTransaction, setSelectedTransaction] =
        useState<ITransactionSearchModel | null>(null);

      const mapCenter = useMemo(() => {
        if (transactionsWithCoordinates.length === 0) {
          return { lat: 37.0902, lng: -95.7129 }; // Default to US center if no coordinates
        }

        // Calculate the average of all coordinates
        const sumLat = transactionsWithCoordinates.reduce(
          (sum: number, transaction: ITransactionSearchModel) =>
            sum + (transaction.latitude || 0),
          0,
        );
        const sumLng = transactionsWithCoordinates.reduce(
          (sum: number, transaction: ITransactionSearchModel) =>
            sum + (transaction.longitude || 0),
          0,
        );

        return {
          lat: sumLat / transactionsWithCoordinates.length,
          lng: sumLng / transactionsWithCoordinates.length,
        };
      }, [transactionsWithCoordinates]);

      const onMapLoad = useCallback(
        (map: google.maps.Map) => {
          const validTransactions = transactionsWithCoordinates;
          if (validTransactions.length === 0) return;
          if (validTransactions.length === 1) {
            map.setCenter({
              lat: validTransactions[0].latitude!,
              lng: validTransactions[0].longitude!,
            });
            map.setZoom(12);
          } else {
            const bounds = new google.maps.LatLngBounds();
            validTransactions.forEach((transaction) => {
              bounds.extend({
                lat: transaction.latitude!,
                lng: transaction.longitude!,
              });
            });
            map.fitBounds(bounds, 50);
          }
        },
        [transactionsWithCoordinates],
      );

      // Google Maps is guaranteed to be loaded at this point thanks to GoogleMapsProvider
      // In MapView, add a visible border and background to the map container for debugging
      return (
        <GoogleMap
          mapContainerStyle={{
            width: "100%",
            height: "100%",
            border: "2px solid red",
            background: "#eee",
          }}
          center={mapCenter}
          zoom={transactionsWithCoordinates.length === 0 ? 6 : undefined}
          onLoad={onMapLoad}
          options={{
            streetViewControl: false,
            mapTypeControl: true,
          }}
        >
          {transactionsWithCoordinates.length === 0 && (
            <div
              style={{
                position: "absolute",
                top: 10,
                left: 10,
                background: "white",
                padding: 8,
                zIndex: 1000,
              }}
            >
              No transactions with coordinates to display.
            </div>
          )}
          {transactionsWithCoordinates.map(
            (transaction: ITransactionSearchModel) => (
              <MarkerF
                key={transaction.id}
                position={{
                  lat: transaction.latitude!,
                  lng: transaction.longitude!,
                }}
                // Pipeline (Lead/Active/Under Contract) entries get a marker in their status colour
                icon={
                  isPipelineStatus(transaction.status)
                    ? {
                        path: google.maps.SymbolPath.CIRCLE,
                        scale: 8,
                        fillColor: statusColor(theme, transaction.status),
                        fillOpacity: 1,
                        strokeColor: "#ffffff",
                        strokeWeight: 2,
                      }
                    : undefined
                }
                title={
                  isPipelineStatus(transaction.status)
                    ? transactionStatusLabel(transaction.status)
                    : undefined
                }
                onClick={() => setSelectedTransaction(transaction)}
              />
            ),
          )}

          {selectedTransaction && (
            <InfoWindowF
              position={{
                lat: selectedTransaction.latitude!,
                lng: selectedTransaction.longitude!,
              }}
              onCloseClick={() => setSelectedTransaction(null)}
            >
              <Box sx={{ maxWidth: 250 }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  {selectedTransaction.formattedAddress}
                </Typography>
                {(selectedTransaction.county || selectedTransaction.state) && (
                  <Typography variant="body2" color="text.secondary">
                    {selectedTransaction.county && selectedTransaction.state
                      ? `${selectedTransaction.county}, ${selectedTransaction.state}`
                      : selectedTransaction.county || selectedTransaction.state}
                  </Typography>
                )}
                <Divider sx={{ my: 1 }} />
                <Typography variant="body2">
                  <strong>Price:</strong>{" "}
                  {formatPrice(selectedTransaction.price)}
                </Typography>
                <Typography variant="body2">
                  <strong>Date:</strong>{" "}
                  {selectedTransaction.date?.format("MMM/DD/YYYY")}
                </Typography>
                <Typography variant="body2">
                  <strong>Type:</strong>{" "}
                  {getTypeName(selectedTransaction.typeId)}
                </Typography>
                {selectedTransaction.sourceId && (
                  <Typography variant="body2">
                    <strong>Source:</strong>{" "}
                    {getSourceName(selectedTransaction.sourceId)}
                  </Typography>
                )}
                <Button
                  variant="outlined"
                  size="small"
                  fullWidth
                  sx={{ mt: 1 }}
                  onClick={() =>
                    navigate(`/transactions/view/${selectedTransaction.id}`)
                  }
                >
                  View Details
                </Button>
              </Box>
            </InfoWindowF>
          )}
        </GoogleMap>
      );
    },
  );

  return (
    <PageLayout>
      <LoadingDialog open={isSearching} />

      <Box sx={{ mb: 6, ml: 1, mr: 1 }}>
        <Box sx={{ mb: 2 }}>
          <Breadcrumbs aria-label="breadcrumb">
            <Link color="inherit" to="/dashboard">
              Dashboard
            </Link>
            <Typography color="text.primary">
              Search & Map Transactions
            </Typography>
          </Breadcrumbs>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography
            variant="h4"
            color="primary"
            component="h1"
            fontWeight="bold"
          >
            Search & Map Transactions
          </Typography>

          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, newMode) => newMode && setViewMode(newMode)}
            aria-label="view mode"
            size="small"
          >
            <ToggleButton value="list" aria-label="list view">
              <ViewListIcon sx={{ mr: isMobile ? 0 : 1 }} />
              {!isMobile && "List View"}
            </ToggleButton>
            <ToggleButton value="map" aria-label="map view">
              <MapIcon sx={{ mr: isMobile ? 0 : 1 }} />
              {!isMobile && "Map View"}
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Search Filters */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Search Criteria
          </Typography>
          <Grid container spacing={2}>
            {/* Year Selection */}
            <Grid item xs={12} sm={6} md={3}>
              <YearFilterSelect
                years={years || []}
                selectedYearIds={searchFilters.yearIds}
                onChange={(yearIds) =>
                  setSearchFilters((prev) => ({ ...prev, yearIds }))
                }
                label={showActive ? "Years/Pipeline" : "Years"}
                pipelineYearId={showActive ? pipelineYearId : undefined}
                pipelineLabel="Active"
                underContractsChecked={searchFilters.includeUnderContracts}
                onUnderContractsChange={
                  showActive
                    ? (includeUnderContracts) =>
                        setSearchFilters((prev) => ({
                          ...prev,
                          includeUnderContracts,
                        }))
                    : undefined
                }
                bulkActions
              />
            </Grid>

            {/* Client Name */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Client Name"
                value={searchFilters.clientName}
                onChange={(e) =>
                  setSearchFilters((prev) => ({
                    ...prev,
                    clientName: e.target.value,
                  }))
                }
                size="small"
              />
            </Grid>

            {/* Address */}
            {/* <Grid item xs={12} sm={6} md={3}>
              <AddressInput
                value={searchFilters.address}
                onChange={(address) =>
                  setSearchFilters((prev) => ({
                    ...prev,
                    address: address,
                  }))
                }
              />
            </Grid> */}

            {/* Zip Code */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Zip Code"
                value={searchFilters.zipCode}
                onChange={(e) =>
                  setSearchFilters((prev) => ({
                    ...prev,
                    zipCode: e.target.value,
                  }))
                }
                size="small"
              />
            </Grid>

            {/* County */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="County"
                value={searchFilters.county}
                onChange={(e) =>
                  setSearchFilters((prev) => ({
                    ...prev,
                    county: e.target.value,
                  }))
                }
                size="small"
              />
            </Grid>

            {/* Transaction Type */}
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Transaction Type</InputLabel>
                <Select
                  multiple
                  value={searchFilters.typeId}
                  label="Transaction Type"
                  onChange={(e) =>
                    setSearchFilters((prev) => ({
                      ...prev,
                      typeId:
                        typeof e.target.value === "string"
                          ? []
                          : (e.target.value as number[]),
                    }))
                  }
                  input={<OutlinedInput label="Transaction Type" />}
                  renderValue={(selected) =>
                    renderLimitedChips(
                      (selected as number[]).map((value) => {
                        const type = userData?.transactionTypes?.find(
                          (t) => t.id === value,
                        );
                        return { key: value, label: type?.name || value };
                      }),
                    )
                  }
                >
                  {userData?.transactionTypes
                    ?.filter((x) => x.deleted !== true)
                    ?.map((type) => (
                      <MenuItem key={type.id} value={type.id}>
                        <Checkbox
                          checked={
                            type.id !== undefined &&
                            searchFilters.typeId.includes(type.id)
                          }
                        />
                        <ListItemText primary={type.name} />
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Source */}
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Source</InputLabel>
                <Select
                  multiple
                  value={searchFilters.sourceId}
                  label="Source"
                  onChange={(e) =>
                    setSearchFilters((prev) => ({
                      ...prev,
                      sourceId:
                        typeof e.target.value === "string"
                          ? []
                          : (e.target.value as number[]),
                    }))
                  }
                  input={<OutlinedInput label="Source" />}
                  renderValue={(selected) =>
                    renderLimitedChips(
                      (selected as number[]).map((value) => {
                        const source = userData?.sources?.find(
                          (s) => s.id === value,
                        );
                        return { key: value, label: source?.name || value };
                      }),
                    )
                  }
                >
                  {userData?.sources
                    ?.filter((x) => x.deleted !== true)
                    ?.map((source) => (
                      <MenuItem key={source.id} value={source.id}>
                        <Checkbox
                          checked={
                            source.id !== undefined &&
                            searchFilters.sourceId.includes(source.id)
                          }
                        />
                        <ListItemText primary={source.name} />
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Reason */}
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Reason</InputLabel>
                <Select
                  multiple
                  value={searchFilters.reasonId}
                  label="Reason"
                  onChange={(e) =>
                    setSearchFilters((prev) => ({
                      ...prev,
                      reasonId:
                        typeof e.target.value === "string"
                          ? []
                          : (e.target.value as number[]),
                    }))
                  }
                  input={<OutlinedInput label="Reason" />}
                  renderValue={(selected) =>
                    renderLimitedChips(
                      (selected as number[]).map((value) => {
                        const reason = userData?.reasons?.find(
                          (r) => r.id === value,
                        );
                        return { key: value, label: reason?.name || value };
                      }),
                    )
                  }
                >
                  {userData?.reasons
                    ?.filter((x) => x.deleted !== true)
                    ?.map((reason) => (
                      <MenuItem key={reason.id} value={reason.id}>
                        <Checkbox
                          checked={
                            reason.id !== undefined &&
                            searchFilters.reasonId.includes(reason.id)
                          }
                        />
                        <ListItemText primary={reason.name} />
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Custom Area */}
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Custom Area</InputLabel>
                <Select
                  multiple
                  value={searchFilters.areaId}
                  label="Custom Area"
                  onChange={(e) =>
                    setSearchFilters((prev) => ({
                      ...prev,
                      areaId:
                        typeof e.target.value === "string"
                          ? []
                          : (e.target.value as number[]),
                    }))
                  }
                  input={<OutlinedInput label="Custom Area" />}
                  renderValue={(selected) =>
                    renderLimitedChips(
                      (selected as number[]).map((value) => {
                        const area = userData?.areas?.find(
                          (a) => a.id === value,
                        );
                        return { key: value, label: area?.name || value };
                      }),
                    )
                  }
                >
                  {userData?.areas?.map((area) => (
                    <MenuItem key={area.id} value={area.id}>
                      <Checkbox
                        checked={
                          area.id !== undefined &&
                          searchFilters.areaId.includes(area.id)
                        }
                      />
                      <ListItemText primary={area.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Special Tags */}
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Special Tags</InputLabel>
                <Select
                  multiple
                  value={searchFilters.specialTags}
                  label="Special Tags"
                  onChange={(e) =>
                    setSearchFilters((prev) => ({
                      ...prev,
                      specialTags:
                        typeof e.target.value === "string"
                          ? []
                          : (e.target.value as number[]),
                    }))
                  }
                  input={<OutlinedInput label="Special Tags" />}
                  renderValue={(selected) =>
                    renderLimitedChips(
                      (selected as number[]).map((value) => {
                        const tag = userData?.specialTags?.find(
                          (t) => t.id === value,
                        );
                        return { key: value, label: tag?.name || value };
                      }),
                    )
                  }
                >
                  {userData?.specialTags?.map((tag) => (
                    <MenuItem key={tag.id} value={tag.id}>
                      <Checkbox
                        checked={
                          tag.id !== undefined &&
                          searchFilters.specialTags.includes(tag.id)
                        }
                      />
                      <ListItemText primary={tag.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Special Groups</InputLabel>
                <Select
                  multiple
                  value={searchFilters.specialGroups}
                  label="Special Groups"
                  onChange={(e) =>
                    setSearchFilters((prev) => ({
                      ...prev,
                      specialGroups:
                        typeof e.target.value === "string"
                          ? []
                          : (e.target.value as number[]),
                    }))
                  }
                  input={<OutlinedInput label="Special Groups" />}
                  renderValue={(selected) =>
                    renderLimitedChips(
                      (selected as number[]).map((value) => {
                        const group = userData?.specialGroups?.find(
                          (g) => g.id === value,
                        );
                        return { key: value, label: group?.name || value };
                      }),
                    )
                  }
                >
                  {userData?.specialGroups?.map((group) => (
                    <MenuItem key={group.id} value={group.id}>
                      <Checkbox
                        checked={
                          group.id !== undefined &&
                          searchFilters.specialGroups.includes(group.id)
                        }
                      />
                      <ListItemText primary={group.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Referral Out Type */}
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Referral Out Type</InputLabel>
                <Select
                  multiple
                  value={searchFilters.referralOutTypeId}
                  label="Referral Out Type"
                  maxRows={1}
                  onChange={(e) =>
                    setSearchFilters((prev) => ({
                      ...prev,
                      referralOutTypeId:
                        typeof e.target.value === "string"
                          ? []
                          : (e.target.value as number[]),
                    }))
                  }
                  input={<OutlinedInput label="Referral Out Type" />}
                  renderValue={(selected) =>
                    renderLimitedChips(
                      (selected as number[]).map((value) => {
                        const type = userData?.referralOutTypes?.find(
                          (t) => t.id === value,
                        );
                        return { key: value, label: type?.name || value };
                      }),
                    )
                  }
                >
                  {userData?.referralOutTypes?.map((type) => (
                    <MenuItem key={type.id} value={type.id}>
                      <Checkbox
                        checked={
                          type.id !== undefined &&
                          searchFilters.referralOutTypeId.includes(type.id)
                        }
                      />
                      <ListItemText primary={type.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Co-Agent */}
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Co-Agent</InputLabel>
                <Select
                  value={searchFilters.coAgentId}
                  label="Co-Agent"
                  onChange={(e) =>
                    setSearchFilters((prev) => ({
                      ...prev,
                      coAgentId: e.target.value as number | "",
                    }))
                  }
                >
                  <MenuItem value="">
                    <em>All Co-Agents</em>
                  </MenuItem>
                  {userData?.coAgents?.map((coAgent) => (
                    <MenuItem key={coAgent.id} value={coAgent.id}>
                      {coAgent.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* SOI Referrer */}
            <Grid item xs={12} sm={6} md={3}>
              <ReferrarAutoComplete
                id="soiReferrers"
                label="SOI Referrer"
                referrerType={ReferrerTypeEnumModel.Soi}
                allowAddNew={false}
                placeholder="Search SOI Referrer"
                helperText={undefined}
                error={undefined}
                selected={searchFilters.soiReferrers}
                onChange={(_e, value) =>
                  setSearchFilters((prev) => ({ ...prev, soiReferrers: value }))
                }
              />
            </Grid>

            {/* Agent Referrer */}
            <Grid item xs={12} sm={6} md={3}>
              <ReferrarAutoComplete
                id="agentReferrers"
                label="Agent Referrer"
                referrerType={ReferrerTypeEnumModel.Agent}
                allowAddNew={false}
                placeholder="Search Agent Referrer"
                helperText={undefined}
                error={undefined}
                selected={searchFilters.agentReferrers}
                onChange={(_e, value) =>
                  setSearchFilters((prev) => ({
                    ...prev,
                    agentReferrers: value,
                  }))
                }
              />
            </Grid>

            {/* Vendor Referrer */}
            <Grid item xs={12} sm={6} md={3}>
              <ReferrarAutoComplete
                id="vendorReferrers"
                label="Vendor Referrer"
                referrerType={ReferrerTypeEnumModel.Vendor}
                allowAddNew={false}
                placeholder="Search Vendor Referrer"
                helperText={undefined}
                error={undefined}
                selected={searchFilters.vendorReferrers}
                onChange={(_e, value) =>
                  setSearchFilters((prev) => ({
                    ...prev,
                    vendorReferrers: value,
                  }))
                }
              />
            </Grid>

            {/* Price Range */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Min Price"
                type="number"
                value={searchFilters.priceMin}
                onChange={(e) =>
                  setSearchFilters((prev) => ({
                    ...prev,
                    priceMin:
                      e.target.value === "" ? "" : Number(e.target.value),
                  }))
                }
                size="small"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Max Price"
                type="number"
                value={searchFilters.priceMax}
                onChange={(e) =>
                  setSearchFilters((prev) => ({
                    ...prev,
                    priceMax:
                      e.target.value === "" ? "" : Number(e.target.value),
                  }))
                }
                size="small"
              />
            </Grid>

            {/* Date Range */}
            <Grid item xs={12} sm={6} md={3}>
              <DatePicker
                label="From Date"
                value={searchFilters.dateFrom}
                onChange={(newValue) =>
                  setSearchFilters((prev) => ({
                    ...prev,
                    dateFrom: newValue,
                  }))
                }
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                  },
                  actionBar: {
                    actions: ["clear"],
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <DatePicker
                label="To Date"
                value={searchFilters.dateTo}
                onChange={(newValue) =>
                  setSearchFilters((prev) => ({
                    ...prev,
                    dateTo: newValue,
                  }))
                }
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                  },
                  actionBar: {
                    actions: ["clear"],
                  },
                }}
              />
            </Grid>
          </Grid>

          <Box
            sx={{
              mt: 2,
              display: "flex",
              gap: 2,
              justifyContent: "flex-start",
            }}
          >
            <Button
              variant="contained"
              startIcon={
                isSearching ? <CircularProgress size={20} /> : <SearchIcon />
              }
              onClick={handleSearch}
              disabled={isSearching || !hasAnySearchCriteria()}
            >
              {isSearching ? "Searching..." : "Search"}
            </Button>
            <Button
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={clearAllFilters}
              disabled={isSearching}
            >
              Clear All
            </Button>
          </Box>
        </Paper>

        {/* Summary Statistics */}
        {hasSearched && searchResults && (
          <Box sx={{ mb: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={2.4}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary">
                      Total Units
                    </Typography>
                    <Typography variant="h5">
                      {summaryStats.totalUnits}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary">
                      Total Volume
                    </Typography>
                    <Typography variant="h5">
                      {formatPrice(summaryStats.totalVolume)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary">
                      Average Price
                    </Typography>
                    <Typography variant="h5">
                      {formatPrice(summaryStats.averagePrice)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary">
                      Total GCI
                    </Typography>
                    <Typography variant="h5">
                      {formatPrice(summaryStats.totalGCI)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary">
                      Total Net Income
                    </Typography>
                    <Typography variant="h5">
                      {formatPrice(summaryStats.totalNetIncome)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error instanceof Error
              ? error.message
              : "An error occurred while searching. Please try again."}
          </Alert>
        )}

        {hasSearched && !isSearching && (
          <>
            {searchResults && searchResults.length > 0 ? (
              <>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Found {searchResults.length ?? 0} transaction
                  {(searchResults.length ?? 0) !== 1 ? "s" : ""}
                </Typography>

                {viewMode === "list" ? (
                  /* List View */
                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Date</TableCell>
                          <TableCell>Client(s)</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell>Address</TableCell>
                          <TableCell>Price</TableCell>
                          <TableCell>Source</TableCell>
                          <TableCell>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {searchResults.map((transaction) => (
                          <TableRow key={transaction.id} hover>
                            <TableCell>
                              {formatDate(transaction.date)}
                              {isPipelineStatus(transaction.status) && (
                                <Chip
                                  label={transactionStatusLabel(
                                    transaction.status,
                                  )}
                                  size="small"
                                  sx={{
                                    ...statusChipSx(theme, transaction.status),
                                    ml: 1,
                                    height: 18,
                                    fontSize: "0.65rem",
                                  }}
                                />
                              )}
                            </TableCell>
                            <TableCell>
                              {transaction.clientNames ?? ""}
                            </TableCell>
                            <TableCell>
                              {getTypeName(transaction.typeId)}
                            </TableCell>
                            <TableCell>
                              <Tooltip
                                title={transaction.formattedAddress || ""}
                              >
                                <span>
                                  {transaction.formattedAddress &&
                                  transaction.formattedAddress.length > 40
                                    ? `${transaction.formattedAddress.substring(
                                        0,
                                        40,
                                      )}...`
                                    : transaction.formattedAddress || "-"}
                                </span>
                              </Tooltip>
                            </TableCell>
                            <TableCell>
                              {formatPrice(transaction.price)}
                            </TableCell>
                            <TableCell>
                              {getSourceName(transaction.sourceId)}
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: "flex", gap: 1 }}>
                                <Tooltip title="View Transaction">
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      handleViewTransaction(transaction.id!)
                                    }
                                  >
                                    <VisibilityIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Edit Transaction">
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      handleEditTransaction(transaction.id!)
                                    }
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  /* Map View */
                  <Paper
                    sx={{
                      p: 0,
                      height: 600,
                      width: "100%",
                      overflow: "hidden",
                    }}
                  >
                    <MapView
                      transactionsWithCoordinates={transactionsWithCoordinates}
                      formatPrice={formatPrice}
                      getTypeName={getTypeName}
                      getSourceName={getSourceName}
                      navigate={navigate}
                    />
                  </Paper>
                )}
              </>
            ) : (
              <Alert severity="info">
                No transactions found matching your search criteria.
              </Alert>
            )}
          </>
        )}
      </Box>
    </PageLayout>
  );
};

export default TransactionSearchAndMapPage;
